import {
  Account,
  AssessmentStatus,
  Category,
  ComplianceSubtype,
  ComplianceType,
  ControlStatus,
  DueType,
  Priority,
  SecondaryPriority,
  TaskStatus,
  Team,
} from 'store/organisations/types';
import { File } from 'store/assets/types';
import {
  FieldResult,
  Nullable,
  TimescaleArray,
  UUID,
  AppContext,
  AssessmentStartDateType,
  AssessmentExpiryDateType,
  DueDeterminedBy,
  AssessmentQuestionType,
  TimescalePeriod,
  AssessmentStatusesEnum,
  FieldTypes,
  FieldRepeatableOrderEnum,
  ISODateString,
} from 'types/types';
import { theme } from 'lib/theme';
import { ICONS } from 'components/Icon';
/**
 * Template
 */

export type Section = {
  id: string;
  name: string;
  question_ids: string[];
  description?: string;
};

export enum AseTplCheckPeriodIdEnum {
  'year' = 'year',
  'month6' = 'month6',
  'quarter' = 'quarter',
  'month' = 'month',
  'week' = 'week',
}

export type AseTplInstanceCheck = {
  id: string;
  name: string;
  type: { id: AseTplCheckPeriodIdEnum; name: string; period: string };
};

export enum FieldInstanceCheckScheduleStatusEnum {
  'waiting' = 'waiting',
  'completed' = 'completed',
  'attempted' = 'attempted',
  'cancelled' = 'cancelled',
  'missed' = 'missed',
}

export enum FieldInstanceCheckStatusEnum {
  'valid' = 'valid',
  'overdue' = 'overdue',
  'cancelled' = 'cancelled',
  'waiting' = 'waiting',
  'endeavour' = 'endeavour',
}

export enum AseFieldInstanceCheckStatusEnum {
  'completed' = 'completed',
  'attempted' = 'attempted',
  'skipped' = 'skipped',
}
export const AseFieldInstanceStatuses: Record<
  AseFieldInstanceCheckStatusEnum,
  {
    id: AseFieldInstanceCheckStatusEnum;
    name: string;
    label: string;
    color: string;
    icon: ICONS;
  }
> = {
  [AseFieldInstanceCheckStatusEnum.completed]: {
    id: AseFieldInstanceCheckStatusEnum.completed,
    name: 'Completed',
    label: 'Completed',
    color: theme.colors.green,
    icon: ICONS.OctagonCheckSolid,
  },
  [AseFieldInstanceCheckStatusEnum.attempted]: {
    id: AseFieldInstanceCheckStatusEnum.attempted,
    name: 'Attempted',
    label: 'Attempted But Not Completed',
    color: theme.colors.red,
    icon: ICONS.OctagonXmarkSolid,
  },
  [AseFieldInstanceCheckStatusEnum.skipped]: {
    id: AseFieldInstanceCheckStatusEnum.skipped,
    name: 'Skipped',
    label: 'Did Not Do',
    color: theme.colors.placeholder,
    icon: ICONS.OctagonMinusSolid,
  },
};

export type Question = {
  id: string;
  question: string;
  level: number;
  answer_ids: string[];
  checklistItemIds?: number[];
  description?: string;
  parent_answer_id?: string;
  criteria_id: string | null;
  type?: AssessmentQuestionType;
};

export type Answer = {
  id: string;
  answer: string;
  negative: boolean;
  parent_question_id: string;
  description: string;
  question_ids: string[];
};

export interface ChecklistItem {
  id: number;
  text: string;
}

export type AssessmentQuestionCriterion = {
  fieldId?: string;
  fieldValue?: string;
  operator: '=' | '!=' | '<' | '<=' | '>' | '>=' | 'includes' | '!includes';
  allowBlank?: boolean;
  allowVarValueBlank?: boolean;
} & (
  | {
      value: string[] | string;
      varValue: never;
      dateModifiers: never;
      varValueDateFormat: never;
    }
  | {
      value: never;
      varValue: string;
      dateModifiers?: { unit: number; period: TimescalePeriod }[];
      varValueDateFormat: string;
    }
);

export type ActionSuggestion = {
  id: string;
  description: string;
  answerIds: string[];
  positiveChecklistItemIds?: number[];
  negativeChecklistItemIds?: number[];
  categoryId: string;
  why: string;
  timescaleId: string;
  severityId?: string;
  dueDeterminedBy: DueDeterminedBy;
  dueTimescale: TimescaleArray | null;
  dueTypeId: string | null;
  cost: string;
};

export type ControlSuggestion = {
  id: string;
  description: string;
  questionIds: string[];
  positiveChecklistItemIds?: number[];
  negativeChecklistItemIds?: number[];
  categoryId: string;
  why: string;
};

type AssessmentTemplate = {
  id: string;
  complianceSubtypeId: string;
  complianceTypeId: string;
  name: string;
  title: string;
  description?: string;
  specification_conforms_to?: string;
  default_remarks_scope: string;
  default_remarks_scope_change: string;
  expiry_date_timescale: TimescaleArray;
  deleted: boolean;
  sections: Record<string, Section>;
  section_ids: string[];
  questions: Record<string, Question>;
  answers: Record<string, Answer>;
  checklistItems: Record<string, ChecklistItem>;
  actionSuggestions: Record<string, ActionSuggestion>;
  controlSuggestions: Record<string, ControlSuggestion>;
  criteria: Record<string, AssessmentQuestionCriterion[][][]>;
  hidePreviousFindings: boolean;
  previousActionsLimitComplianceTypes?: string[] | null;
  previousControlsLimitComplianceTypes?: string[] | null;
  requireAllAnswers: boolean;
  fieldInstanceChecks: Record<string, AseTplInstanceCheck>;
  // fields introduced 2022-07-01
  fields: {
    groups: Record<
      string,
      {
        questionIds: string[];
        checkIds: string[];
      }
    >;
    fields: Record<string, { edit: boolean; require: boolean }>;
  };
  settings: {
    editAssetName: boolean;
    editAssetAddress: boolean;
    editAssetAvatar: boolean;
    editAssetCustomReference: boolean;
    questionComments: boolean;
    addCustomActions: boolean;
    addCustomControls: boolean;
  };
  flags: {
    id: string;
    name: string;
    description: string;
    allowedStatuses: AssessmentStatusesEnum[];
  }[];
};

/**
 * Results
 */

export type InstanceParentParams =
  | { fieldId: string; instanceId: UUID }
  | { fieldId: string; instanceId: null }
  | null;

export type SectionResult = {
  id: string;
  comment: string;
};

export type QuestionResult = {
  id: string;
  answerId: string;
  comment: string;
};

export enum ChecklistItemResultEnum {
  'tick' = 'tick',
  'cross' = 'cross',
  'dash' = 'dash',
}

export type ChecklistItemResult = {
  id: number;
  result: ChecklistItemResultEnum;
};

export interface FindingHistory {
  comment: string | null;
  status: { id: string; name: string } | null;
  statusConfirmed: boolean;
  fileIds: string[];
  by: Account;
  ts: number;
}

export interface FindingPreviousParent {
  id: string;
  type: 'question' | 'section';
}

export type Action = {
  id: string;
  reference: string;
  customReference: string;
  questionId: string | null;
  sectionId: string | null;
  suggestionId: string | null;
  fieldInstanceId: string | null;
  description: string;
  where: string;
  why: string;
  categoryId: string;
  timescaleId: string;
  severityId: string;
  assignedAccountIds: string[];
  assignedTeamIds: string[];
  dueDate: string | null;
  dueDeterminedBy: DueDeterminedBy;
  dueTimescale: TimescaleArray | null;
  dueTypeId: string | null;
  photoIds: string[];
  quantity: string;
  isReview: boolean;
  reviewNote: string;
  reviewStatusId: string | null;
  previousParents: FindingPreviousParent[];
  complianceTypeId: string;
  complianceSubtypeId: string;
  history: FindingHistory[];
  milestone?: number; // timestamp to be used by app
  cost: string;
  pin: {
    planId: string;
    x: string;
    y: string;
  } | null;
};

export type Control = {
  id: string;
  reference: string;
  suggestionId: string | null;
  questionId: string | null;
  sectionId: string | null;
  fieldInstanceId: string | null;
  description: string;
  where: string;
  why: string;
  categoryId: string;
  photoIds: string[];
  isReview: boolean;
  reviewNote: string;
  previousParents: FindingPreviousParent[];
  reviewStatusId: string | null;
  complianceTypeId: string;
  complianceSubtypeId: string;
  history: FindingHistory[];
  milestone?: number; // timestamp to be used by app
};

export type NewControl = Control;
export type NewAction = Action;

export interface FindingSuggestion {
  type: 'action' | 'control';
  questionId: string;
  description: string;
  why: string;
  categoryId: string;
  timescaleId: string;
  severityId: string;
  dueDeterminedBy: DueDeterminedBy;
  dueTimescale: TimescaleArray | null;
  dueTypeId: string | null;
  answerId: string;
  checklistItemId: number;
  cost: string;
}

export type RiskOutcome = {
  likelihood: {
    name: string;
    description: string;
  };
  severity: {
    name: string;
    description: string;
  };
  rating: {
    name: string;
    description: string;
    colour: string;
  };
};

export type Details = {
  statusId: AssessmentStatusesEnum;
  // dueDate: string | null;
  // latestApproved: boolean;
  // statusSummaryLabel: string;
  assessedDate: string;
  assessedTime: string;
  assessedBy: (Account & { role: string })[];
  customReference: string;
  startDateType: AssessmentStartDateType;
  startDate: string;
  expiryDateType: AssessmentExpiryDateType;
  expiryDate: string;
  expiryDateTimescale: TimescaleArray | null;
  approvedDate: string | null;
  approvedBy: Account[] | null;
  actionTimescaleDate: string;
  actionTimescaleUpdateReviewed: boolean;
  // templateId: string;
  risk: RiskOutcome | null;
  riskLikelihoodId: string;
  riskSeverityId: string;
  riskChangeReason: boolean;
  riskReason: string;
  remarksScope: string;
  remarksScopeChange: string;
  remarksAssessor: string;
  fileIds: string[];
  authorOrganisation: { id: string; name: string };
};

type AssetDetailsRead = {
  reference: string;
  parentName: string;
};

type AssetDetailsEdit = {
  name: string;
  customReference: string;
  address1: string;
  address2: string;
  city: string;
  county: string;
  postcode: string;
  phone: string;
  avatar: string;
};

export type AseResultInstanceCheck = {
  checkId: string;
  instanceId: string;
  remarks: string;
  statusId: AseFieldInstanceCheckStatusEnum;
};

export type AseFieldInstanceCheckAvailability = {
  id: string;
  startDateTime: ISODateString;
  endDateTime: ISODateString;
};

export type AseFieldInstanceCheck = {
  id: string;
  lastCompleted: null | { date: number };
  lastAttempted: null | { date: number };
  overdue: boolean;
  schedule: {
    due: { from: number; to: number };
    statusId:
      | FieldInstanceCheckScheduleStatusEnum.attempted
      | FieldInstanceCheckScheduleStatusEnum.completed
      | null;
  };
  availability: AseFieldInstanceCheckAvailability[];
};

export type AseFieldInstance = {
  instanceId: string;
  // @added 6.28 (2023-08-03) could not upgrade local data
  reference?: string;
  groupId: string;
  createdTs: number;
  updatedTs: number;
  /**
   * To delete a field result, set results[string].value=null
   */
  results: Record<string, FieldResult>;
  /**
   * Result autoId 0 if draft in assessment
   */
  autoId: number;
  /**
   * Used within assessments whilst a repeatable is draft. If this equals 2
   * and the max approved autoId is 3, the draft repeatable will get an autoId of 3+2=5
   */
  draftAutoId?: number;
  draft: boolean;
  // Delete to delete instance, remove to remove instance from assessment
  action: null | 'delete' | 'remove';
  checks: Record<string, AseFieldInstanceCheck>;
};

export type AseFieldInstanceWithFormattedAutoId = AseFieldInstance & {
  results: Record<string, FieldResult>;
  formattedAutoId?: string;
  inAssessment: boolean;
};

export type AseSignatory = {
  signatoryId: string;
  name: string;
  signature: string;
  unavailable: boolean;
  timestamp: number;
};

export type AssessmentResults = {
  id: string;
  reference: string;
  statusSummaryLabel: string;
  details: Details;
  asset: Partial<Nullable<AssetDetailsEdit>>;
  fieldResults: Record<string, FieldResult>;
  fieldInstances: Record<string, AseFieldInstance>;
  editable: boolean;
  sections: Record<string, SectionResult | null>;
  questions: Record<string, QuestionResult | null>;
  checklistItems: Record<string, ChecklistItemResult | null>;
  questionComments: Record<string, string>;
  actions: Record<string, Action | null>;
  controls: Record<string, Control | null>;
  fieldInstanceChecks: Record<string, AseResultInstanceCheck>;
  signatories: Record<string, AseSignatory | null>;
  flags: Record<
    string,
    {
      flagId: string;
      comment: string;
      by: { name: string; email: string };
      timestamp: number;
    }
  >;
  flagHistory: {
    flagId: string;
    name: string;
    description: string;
    created: {
      comment: string;
      by: { name: string; email: string };
      timestamp: number;
    };
    deleted: {
      comment: string;
      by: { name: string; email: string };
      timestamp: number;
    } | null;
  }[];
};

export enum AssessmentItemTypes {
  ACTION = 'actions',
  CONTROL = 'controls',
  QUESTION = 'questions',
  CHECKLISTITEM = 'checklistItems',
  SECTION = 'sections',
  SUMMARY = 'summary',
  QUESTIONCOMMENTS = 'questionComments',
}

export type ExplorerOptionsProps = {
  size: 'normal' | 'small';
  // view: 'default' | 'detailed' | 'compact';
  questionDescriptions: 'full' | 'short';
  findingDescriptions: 'full' | 'short';
  findingSuggestionDescriptions: 'full' | 'short';
  findingAdditional: boolean;
  findingSuggestionAdditional: boolean;

  questionsUnanswered: boolean;
  questionsNegative: boolean;
  questionsPositive: boolean;
  findingsNewReviewed: boolean;
  findingsUnreviewed: boolean;
  findingShowReference: boolean; // @added 2022-07-19
  findingsCollapse: boolean;

  showValidationErrors: boolean;
};

// Fields
export type AseTplFieldField = {
  id: string;
  type: FieldTypes;
  label: string;
  description: string;
  defaultValue: string;
  multiSelect?: boolean;
  minValue: string | null;
  maxValue: string | null;
  options: {
    id: string;
    name: string;
  }[];
  criteria: {
    fieldId: string;
    fieldValue: string;
    operator: '=' | '!=' | '<' | '<=' | '>' | '>=' | 'includes' | '!includes';
    allowBlank?: boolean;
  }[];
  suggestions: string[];
  childGroupId: string | null;
};
export type AseTplFieldGroup = {
  id: string;
  name: string;
  repeatable: boolean;
  // fields: Record<string, AseTplFieldField>;
  fieldIds: string[];
  nameSingular: string;
  autoIdPrefix: string;
  autoIdZeroFill: number;
  disableAutoId: boolean;
  allowPdfDownload: boolean;
  // checkIds: string[];

  calculated: {
    fieldsHaveCriteria: boolean;
    requiredFieldIds: string[];
  };
};
export type AseTplFieldSection = {
  id: string;
  name: string;
  groupIds: string[];
  // groups: Record<string, AseTplFieldGroup>;
};
export type AseTplFieldGroupWithCalculatedInfo = AseTplFieldGroup & {
  calculatedInfo: {
    singularName: string;
    stringFieldIds: string[];
    textFieldIds: string[];
    textareaFieldIds: string[];
    numberFieldIds: string[];
    optionFieldIds: string[];
    imageFieldId: string | undefined;
    qrFieldId: string | undefined;
    pinFieldId: string | undefined;
  };
};

export type AssessmentSignatory = {
  id: string;
  name: string;
  declaration: string;
  requireOnSubmit: boolean;
  allowUnavailable: boolean;
};

export type AvailabilityCategoryText = 'Today' | 'Tomorrow' | 'Next 7 Days';
export type AssessmentFilterGroup = {
  search: string;
  order: FieldRepeatableOrderEnum | string;
  checks: Record<string, 'required' | 'notRequired'>;
  requiredFields: boolean;
  availabilityCategories: AvailabilityCategoryText[];
};

export type Assessment = {
  id: string;
  isOnline: boolean;
  assetId: string;
  organisationId: UUID;
  showValidationErrors: boolean;
  // complianceSubtypeId: string;

  tempData: { repeatables: Record<string, AseFieldInstance> };

  filterGroups: Record<string, AssessmentFilterGroup>;

  features: {
    edit: boolean;
    editassetplans: boolean;
    unsubmit: boolean;
    approve: boolean;
    unapprove: boolean;
    downloadBundle: boolean;
  };

  template: AssessmentTemplate;
  results: AssessmentResults;
  stats: {
    fieldInstanceRequiredResultsCount: Record<string, number>;
    fieldGroupRequiredResultsCount: Record<string, number>;
  };

  task: {
    dueDate: number | null;
    description: string;
  } | null;

  assetData: {
    details: AssetDetailsRead & AssetDetailsEdit;
    actions: Action[];
    controls: Control[];
    fieldResults: Record<string, FieldResult>;
    fieldInstances: Record<string, AseFieldInstance>;
    fieldInstanceAutoIds: Record<string, number>;
    defaultInstanceChecks: Record<
      string,
      Record<string, AseFieldInstanceCheck>
    >;
    files: Record<string, File>;
    answers: Record<string, string>;
    assessmentTimestamps: number[];
    riskRatings: Record<string, [string, string, string]>;
    plans: Record<
      string,
      {
        id: string;
        name: string;
        fileId: string;
        fileType: 'image' | 'pdf' | 'pdfpage' | 'svg' | 'svgpdf';
        filePage: number;
        fileWidth?: number;
        fileHeight?: number;
        pinCount: number;
      }
    >;
    teamResponsibility: Record<string, string[]>;
  };

  otherData: {
    accounts: Record<string, Account>;
    teams: Record<string, Team>;
    assessment_statuses: Record<string, AssessmentStatus>;
    compliance_types: Record<string, ComplianceType>;
    compliance_sub_types: Record<string, ComplianceSubtype>;
    categories: Record<string, Category>;
    priorities: Record<string, Priority>;
    secondary_priorities: Record<string, SecondaryPriority>;
    due_types: Record<string, DueType>;
    task_statuses: TaskStatus[];
    control_statuses: ControlStatus[];
    fields: {
      sectionIds: string[];
      sections: Record<string, AseTplFieldSection>;
      groups: Record<string, AseTplFieldGroup>;
      fields: Record<string, AseTplFieldField>;
    };
    allowedAuthors: {
      id: string;
      name: string;
      email: string;
    }[];
    allowedAuthorOrganisations: { id: string; name: string }[];
    signatories: AssessmentSignatory[];
  };
};

export interface AssessmentState {
  isOpen: boolean;
  context?: AppContext;
  collapsedSections: Record<string, boolean>;
  assessment?: Assessment;
}

export interface LoadAssessment {
  assetId: string;
  assessmentId: string;
}

export interface LoadAssessmentPDF {
  live?: boolean;
  doShare?: boolean;
}

export type LoadAssessmentBundleZip = {
  doShare?: boolean;
};

export interface LoadAssessmentElementPDF {
  elementId: UUID;
  doShare?: boolean;
}

export type ChecklistItemHasSuggestedActions = Record<
  string,
  {
    positive: ActionSuggestion[];
    negative: ActionSuggestion[];
    na: ActionSuggestion[];
  }
>;
export type ChecklistItemHasSuggestedControls = Record<
  string,
  {
    positive: ControlSuggestion[];
    negative: ControlSuggestion[];
    na: ControlSuggestion[];
  }
>;
